import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import apiCalls from 'src/api/apiCalls';
import moment from 'moment';
import NoData from 'src/components/shared/noDataComponent';
import ErrorMessage from 'src/components/shared/messages/errorMessage';
import Loader from 'src/components/shared/loader.component';
import PreviewModal from 'src/components/shared/preview.model';
import { Image } from 'antd';
import { DownloadButton } from '../../service';
import ComponentsBreadcrumb from 'src/layout/componentsBreadcrumb';

const KYCImageCard = ({ title, kycDocInfo, renderKYCImage }) => (
  <div className=''>
    <h2 className="text-titleColor font-semibold text-2xl mb-3 ">{title}</h2>
    {(kycDocInfo?.atmHoldingIdPhoto || kycDocInfo?.backIdPhoto || kycDocInfo?.frontIdPhoto || kycDocInfo?.faceImage || kycDocInfo?.handHoldingIdPhoto || kycDocInfo?.signaturePhoto) === null ? <NoData /> : <div className='border border-StrokeColor rounded-5 p-3.5 grid xl:grid-cols-2 md:grid-cols-2 gap-4'>
      {renderKYCImage('Front ID Photo', kycDocInfo?.frontIdPhoto)}
      {renderKYCImage('Back ID Photo', kycDocInfo?.backIdPhoto)}
      {renderKYCImage('Hand Holding ID Photo', kycDocInfo?.handHoldingIdPhoto)}
      {renderKYCImage('ATM Holding ID Photo', kycDocInfo?.atmHoldingIdPhoto)}
      {renderKYCImage('Face Image', kycDocInfo?.faceImage)}
      {renderKYCImage('Signature', kycDocInfo?.signaturePhoto)}
    </div>}
  </div>
);

const KycDetails = () => {
  const navigate = useNavigate()
  const { id, programId } = useParams()
  const [localState, setLocalState] = useState({ details: null, loader: false, error: '' })
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState("");
  useEffect(() => {
    getKycDetails()
  }, [])
  const getKycDetails = async () => {
    setLocalState(state => ({ ...state, loader: true }))
    try {
      const response = await apiCalls.getCustomerKycInfo(`KycService/Customer/Kyc/${id}/${programId}`);
      if (response?.ok) {
        setLocalState(state => ({ ...state, details: response?.data }))
        localState.error && setLocalState(state => ({ ...state, error: '' }))
      }
      else {
        setLocalState(state => ({ ...state, error: apiCalls.isErrorDispaly(response) }))
      }
    }
    catch (error) {
      setLocalState(state => ({ ...state, error: apiCalls.isErrorDispaly(error) }))
    } finally {
      setLocalState(state => ({ ...state, loader: false }))
    }
  }
  const navigateToGrid = () => {
    navigate(`/customer/${id}/customerKyc`);
  }
  const breadCrumbList = useMemo(() => [
    { id: '1', bname: 'KYC', url: `/customer/${id}/customerKyc` },
    { id: '2', bname: localState?.details?.programName },
  ], [localState?.details]);
  const handleDownload = useCallback((fileUrl) => () => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileUrl.split('/').pop(); // Extracts the filename from the URL
      link.click();
    } else {
      console.error("Download URL is not available.");
    }
  }, [])
  const onLoadImage = useCallback((imageUrl) => (e) => {
    e.target.src = imageUrl;
    e.target.style.filter = 'none';
  }, [])
  const downloadButton = useCallback((imageUrl) => {
    return <DownloadButton imageUrl={imageUrl} handleDownload={handleDownload} />
  }, [handleDownload])

  const renderKYCImage = useCallback((label, imageUrl) => {
    if (!imageUrl) return null;

    const handleImageLoad = onLoadImage(imageUrl);
    const renderDownloadButton = downloadButton(imageUrl);

    return (
      <div className=''>
        <div className="">
          <p className="mb-0.5 text-labelGrey">{label}</p>
          <div className="relative kyc-preview group bg-overlay">
            <Image
              src={`${imageUrl}?w=200&blur=20`}
              className="mb-1 mx-auto rounded-5 !object-cover"
              alt={label}
              style={{ width: '100%', height: '184px' }}
              onLoad={handleImageLoad}
              preview={{
                toolbarRender: () => renderDownloadButton,
              }}
            />
          </div>
        </div>
      </div>
    );
  }, [onLoadImage, downloadButton]);

  const handleClosePreview = useCallback(() => {
    setIsPreviewVisible(false);
    setPreviewFile("");
  }, [])

  const closeErrorHandler = useCallback(() => {
    setLocalState(state => ({ ...state, error: '' }))
  }, [])

  return (
    <div>
      {localState.loader ? <Loader /> : <div>

        {localState.error && <ErrorMessage msg={localState.error} onClose={closeErrorHandler} />}
        {localState.details && <>
          <div className=''>
            <ComponentsBreadcrumb breadCrumbList={breadCrumbList} />
            <h1 className='text-titleColor font-semibold text-2xl mb-3'>KYC Information</h1>
            <div className='grid md:grid-cols-4 gap-4 mb-4'>
              <div className="">
                <p className="summary-label">Document Type</p>
                <p className="summary-value">{localState.details?.documentType || ""}</p>
              </div>
              <div className="">
                <p className="summary-label">Document Number</p>
                <p className="summary-value">{localState.details?.documentNumber || ""}</p>
              </div>
              <div className="">
                <p className="summary-label">Expiry Date</p>
                <p className="summary-value">{localState.details?.expiryDate ? moment(localState.details?.expiryDate).format('DD/MM/YYYY') : ""}</p>
              </div>
              <div className="">
                <p className="summary-label">ID Issuing Country</p>
                <p className="summary-value">{localState.details?.idIssusingCountry || ""}</p>
              </div>
              <div className="">
                <p className="summary-label">Emergency Contact Name</p>
                <p className="summary-value">
                  {localState.details?.emergencyContactPerson || ''}
                </p>
              </div>
              {localState.details?.language && <div className="mb-3">
                <p className="summary-label">Language</p>
                <p className="mb-0 tab-val summary-value">{localState.details?.language || ""}</p>
              </div>}
            </div>
          </div>
          {localState.details && <KYCImageCard title="Identification Documents" kycDocInfo={localState.details} renderKYCImage={renderKYCImage} />}
          <div className='text-right mt-4 mb-4'><button type='cancel' onClick={navigateToGrid} className='primary-btn px-4 py-1 rounded-5 font-medium'>Back</button></div>
        </>}
        {!localState.details && <NoData />}
      </div>}
      {isPreviewVisible && <PreviewModal
        isVisible={isPreviewVisible}
        fileUrl={previewFile}
        onClose={handleClosePreview}
      />}
    </div>
  )
}
export default KycDetails